import React, { useState } from 'react';
import { Upload, Download, Loader2, AlertTriangle, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { contentCalendarAPI } from '../lib/api';
import { getStateMeta } from '../lib/contentStates';

const COLUMNS = ['fecha', 'titulo', 'estado', 'tipo', 'canales', 'pilar', 'copy', 'guion', 'instrucciones',
  'quien_graba', 'automatizacion', 'responsable', 'responsable_portada', 'aprobado_cliente'];

const downloadTemplate = () => {
  const blob = new Blob(['\uFEFF' + COLUMNS.join(';') + '\n'], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'plantilla_calendario_contenido.csv';
  a.click();
  URL.revokeObjectURL(a.href);
};

const ResultList = ({ title, Icon, tone, items, render, testId }) => (items?.length ? (
  <div className={`rounded-md border p-2 ${tone}`} data-testid={testId}>
    <p className="text-xs font-semibold flex items-center gap-1 mb-1"><Icon className="w-3.5 h-3.5" />{title} ({items.length})</p>
    <ul className="text-xs space-y-0.5 max-h-36 overflow-y-auto">{items.map((it, i) => <li key={i}>{render(it)}</li>)}</ul>
  </div>
) : null);

export const CsvImportButton = ({ clientId, clientName, onImported }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [silent, setSilent] = useState(true);

  const reset = () => { setFile(null); setCheck(null); setSilent(true); };
  const run = async (dryRun) => {
    setBusy(true);
    try {
      const r = await contentCalendarAPI.importCsv(clientId, file, dryRun, !silent);
      if (dryRun) setCheck(r);
      else { toast.success(`${r.created} publicaciones importadas`); setOpen(false); reset(); onImported?.(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="csv-import-btn"><Upload className="w-4 h-4 mr-1.5" />Importar CSV</Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-xl" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle>Importar publicaciones · {clientName}</DialogTitle>
            <DialogDescription>Un CSV por cliente. Obligatorios: fecha y título. Primero se revisa; no se crea nada hasta que confirmes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button variant="link" size="sm" className="px-0" onClick={downloadTemplate} data-testid="csv-template-btn"><Download className="w-4 h-4 mr-1" />Descargar plantilla vacía</Button>
            <input type="file" accept=".csv,text/csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setCheck(null); }} className="block text-sm" data-testid="csv-file-input" />
            {check && (
              <div className="space-y-2" data-testid="csv-check-result">
                <p className="text-sm flex items-center gap-1.5 font-medium text-emerald-700" data-testid="csv-valid-count"><CheckCircle2 className="w-4 h-4" />{check.valid} publicaciones listas para importar</p>
                <ResultList title="Filas con errores (no se importan)" Icon={AlertTriangle} tone="border-red-200 bg-red-50 text-red-800" items={check.errors} testId="csv-errors"
                  render={(e) => <>Fila {e.row}{e.titulo ? ` («${e.titulo}»)` : ''}: {e.error}</>} />
                <ResultList title="Ya existen (misma fecha y título, se saltan)" Icon={Copy} tone="border-amber-200 bg-amber-50 text-amber-800" items={check.duplicates} testId="csv-duplicates"
                  render={(d) => <>Fila {d.row}: {d.fecha} · {d.titulo}</>} />
                {check.unknown_columns?.length > 0 && <p className="text-xs text-muted-foreground">Columnas ignoradas: {check.unknown_columns.join(', ')}</p>}
                {check.preview?.length > 0 && (
                  <ul className="text-xs max-h-40 overflow-y-auto border rounded-md divide-y" data-testid="csv-preview">
                    {check.preview.map((p) => (
                      <li key={p.row} className="flex items-center gap-2 px-2 py-1">
                        <span className="text-muted-foreground w-20 shrink-0">{p.fecha}</span>
                        <span className="truncate flex-1">{p.titulo}</span>
                        <span className="px-1.5 rounded" style={{ background: getStateMeta(p.estado).bg, color: getStateMeta(p.estado).fg }}>{getStateMeta(p.estado).label}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <label className="flex items-start gap-2 text-sm rounded-md border p-2 cursor-pointer" data-testid="csv-silent-label">
                  <Checkbox checked={silent} onCheckedChange={(v) => setSilent(!!v)} data-testid="csv-silent-checkbox" className="mt-0.5" />
                  <span>
                    <b>No avisar a los clientes</b>
                    <span className="block text-xs text-muted-foreground">
                      {silent
                        ? 'Las tareas del equipo se crean igual. Al cliente no le llega ningún aviso ni recordatorio de estas publicaciones; si quieres avisarle luego, usa «Recordar al cliente».'
                        : 'Se crean como a mano: tareas para el equipo y aviso al cliente si están en «Pendiente de aprobar».'}
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            {!check
              ? <Button onClick={() => run(true)} disabled={!file || busy} data-testid="csv-check-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Revisar CSV</Button>
              : <Button onClick={() => run(false)} disabled={busy || !check.valid} data-testid="csv-confirm-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Importar {check.valid} publicaciones</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
